import React, { useState, useEffect } from "react";
import { collection, query, getDocs, orderBy, limit, Timestamp } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { VotingProject } from "../../services/votingService";

interface VoteRecord {
  id: string;
  projectId: string;
  deviceFingerprint: string;
  votedAt: Timestamp;
}

interface VotingActivityProps {
  projects: VotingProject[];
}

const VotingActivity: React.FC<VotingActivityProps> = ({ projects }) => {
  const [recentVotes, setRecentVotes] = useState<VoteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalVotes: 0,
    votesToday: 0,
    votesThisWeek: 0,
    uniqueDevices: 0,
  });

  useEffect(() => {
    loadRecentVotes();
    loadStats();
    
    // Set up real-time polling (refresh every 10 seconds)
    const interval = setInterval(() => {
      loadRecentVotes();
      loadStats();
    }, 10000);

    return () => clearInterval(interval);
  }, [projects]);

  const loadRecentVotes = async () => {
    try {
      const votesRef = collection(db, "voteRecords");
      const q = query(votesRef, orderBy("votedAt", "desc"), limit(50));
      const querySnapshot = await getDocs(q);
      
      const votes = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as VoteRecord[];
      
      setRecentVotes(votes);
    } catch (error) {
      console.error("Error loading recent votes:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const votesRef = collection(db, "voteRecords");
      const allVotesSnapshot = await getDocs(votesRef);
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      let votesToday = 0;
      let votesThisWeek = 0;
      const uniqueDevices = new Set<string>();
      
      allVotesSnapshot.docs.forEach((doc) => {
        const vote = doc.data() as VoteRecord;
        const votedAt = vote.votedAt?.toMillis() || 0;
        const voteDate = new Date(votedAt);
        
        uniqueDevices.add(vote.deviceFingerprint);
        
        if (voteDate >= today) {
          votesToday++;
        }
        if (voteDate >= weekAgo) {
          votesThisWeek++;
        }
      });
      
      setStats({
        totalVotes: allVotesSnapshot.size,
        votesToday,
        votesThisWeek,
        uniqueDevices: uniqueDevices.size,
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const getProjectName = (projectId: string): string => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || "Unknown Project";
  };

  const formatDeviceFingerprint = (fingerprint: string): string => {
    return fingerprint.substring(0, 8) + "...";
  };

  const formatDate = (timestamp: Timestamp | undefined): string => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp.toMillis());
    return date.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-xl p-6 shadow-lg border border-[rgba(59,130,246,0.2)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-1">
                Total Votes
              </p>
              <p className="text-2xl font-bold text-[var(--text-primary)]">
                {stats.totalVotes}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--gradient-primary)] to-[var(--gradient-secondary)] flex items-center justify-center">
              <i className="fa-solid fa-heart text-white text-lg"></i>
            </div>
          </div>
        </div>

        <div className="bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-xl p-6 shadow-lg border border-[rgba(59,130,246,0.2)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-1">
                Votes Today
              </p>
              <p className="text-2xl font-bold text-[var(--text-primary)]">
                {stats.votesToday}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
              <i className="fa-solid fa-calendar-day text-white text-lg"></i>
            </div>
          </div>
        </div>

        <div className="bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-xl p-6 shadow-lg border border-[rgba(59,130,246,0.2)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-1">
                Votes This Week
              </p>
              <p className="text-2xl font-bold text-[var(--text-primary)]">
                {stats.votesThisWeek}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
              <i className="fa-solid fa-calendar-week text-white text-lg"></i>
            </div>
          </div>
        </div>

        <div className="bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-xl p-6 shadow-lg border border-[rgba(59,130,246,0.2)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-1">
                Unique Devices
              </p>
              <p className="text-2xl font-bold text-[var(--text-primary)]">
                {stats.uniqueDevices}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
              <i className="fa-solid fa-mobile-screen-button text-white text-lg"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Votes Table */}
      <div className="bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-xl shadow-lg border border-[rgba(59,130,246,0.2)]">
        <div className="p-6 border-b border-[var(--border-color)]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-[var(--text-primary)]">
              Recent Voting Activity
            </h2>
            <button
              onClick={() => {
                loadRecentVotes();
                loadStats();
              }}
              className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--primary-color-hover)] text-white rounded-lg transition-colors text-sm font-medium"
            >
              <i className="fa-solid fa-sync mr-2"></i>
              Refresh
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center text-[var(--text-secondary)]">
              Loading activity...
            </div>
          ) : recentVotes.length === 0 ? (
            <div className="p-12 text-center text-[var(--text-secondary)]">
              No voting activity yet.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[var(--background-primary)]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                    Device ID
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-color)]">
                {recentVotes.map((vote) => (
                  <tr
                    key={vote.id}
                    className="hover:bg-[var(--background-primary)] transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]">
                      {formatDate(vote.votedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-[var(--text-primary)]">
                        {getProjectName(vote.projectId)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-[rgba(59,130,246,0.1)] text-[var(--primary-color)] text-xs font-mono">
                        {formatDeviceFingerprint(vote.deviceFingerprint)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default VotingActivity;

